import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import { useGuest } from '../context/GuestContext';
import '../styles/CountdownTimer.css';

const GOOGLE_CAL_URL = 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
  '&text=Amiker+%26+Gergely%27s+Wedding' +
  '&dates=20270703T140000Z/20270704T000000Z' +
  '&details=Join+us+to+celebrate+our+wedding!' +
  '&location=Budapest,+Hungary';

const downloadICS = () => {
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//A&G Wedding//EN',
    'BEGIN:VEVENT',
    'DTSTART:20270703T160000',
    'DTEND:20270704T020000',
    'SUMMARY:Amiker & Gergely\'s Wedding',
    'DESCRIPTION:Join us to celebrate our wedding!',
    'LOCATION:Budapest\\, Hungary',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    'DESCRIPTION:Wedding tomorrow!',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-P7D',
    'ACTION:DISPLAY',
    'DESCRIPTION:Wedding in one week!',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'amiker-gergely-wedding.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CountdownTimer = () => {
  const { t } = useTranslation();
  const { guest } = useGuest();
  const weddingDate = new Date('2027-07-03T15:00:00');

  function calculateTimeLeft() {
    const difference = +weddingDate - +new Date();
    if (difference <= 0) return null;
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const units = ['days', 'hours', 'minutes', 'seconds'];

  return (
    <section id="countdown-timer" className="countdown-section">
      <motion.h2
        className="countdown-title"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {guest?.special === 'amiker'
          ? "Countdown to Becoming a Mrs!"
          : guest?.special === 'gergely'
          ? "Countdown to the Best Day of Your Life"
          : t('countdown.title')}
      </motion.h2>

      {timeLeft ? (
        <motion.div
          className="countdown-units"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {units.map((unit, i) => (
            <div key={unit} className="countdown-unit">
              <span className="countdown-number">
                {String(timeLeft[unit]).padStart(unit === 'days' ? 1 : 2, '0')}
              </span>
              <span className="countdown-label">{t(`countdown.${unit}`)}</span>
            </div>
          ))}
        </motion.div>
      ) : (
        <motion.p
          className="countdown-today"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {t('countdown.todaysTheDay')}
        </motion.p>
      )}

      <motion.p
        className="countdown-message"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {guest?.special === 'amiker'
          ? "Time to start panicking about seating arrangements"
          : guest?.special === 'gergely'
          ? "Just enough time to learn your first dance"
          : t('countdown.message')}
      </motion.p>

      <motion.div
        className="countdown-calendar-buttons"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <button
          onClick={() => window.open(GOOGLE_CAL_URL, '_blank')}
          className="countdown-cal-btn"
          aria-label={t('navbar.addToCalendar')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>{t('navbar.addToCalendar')}</span>
        </button>
        <button
          onClick={downloadICS}
          className="countdown-cal-btn"
          aria-label={t('navbar.downloadCard')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>{t('navbar.downloadCard')}</span>
        </button>
      </motion.div>
    </section>
  );
};

export default CountdownTimer;
